import React, { FC, ReactElement, useCallback, useState } from "react";
import styles from "./ManuellRegistreringsflyt.module.less";
import { useSteps } from "../infrastructure/steps/useSteps";
import { useIntegrasjonsPartner } from "../PartnerOnboarding/useIntegrasjonsPartner";
import {
  ManuellKontaktinfo,
  useRegistrerVirksomhet,
} from "../Registreringsflyt/useRegistrerVirksomhet";
import { LoadingPage } from "../LoadingPage/LoadingPage";
import { Outlet } from "react-router";
import { EnkelVirksomhetInfo } from "../infrastructure/api/api";
import { useUnrecoverableError } from "../infrastructure/errors/useUnrecoverableError";
import { AnalyticsCategory } from "../infrastructure/analytics/types";
import { analyticsAction } from "../infrastructure/analytics/functions";

export type ManuellRegistreringContextType = {
  kontaktInfo?: ManuellKontaktinfo;
  submitKontaktinfo: (manuellKontaktinfo: ManuellKontaktinfo) => void;
  integrasjonsPartner?: EnkelVirksomhetInfo;
  registrer: () => void;
  previousStep: () => void;
  nextStep: () => void;
  reset: () => ReactElement;
};

const ManuellRegistreringsflyt: FC<{ steps: string[] }> = ({
  steps,
}): React.ReactElement => {
  const { previousStep, nextStep, reset } = useSteps(steps);
  const [kontaktInfo, setKontaktInfo] = useState<ManuellKontaktinfo>();
  const { setCustomError } = useUnrecoverableError();

  const { isRegistering, registrerVirksomhetManuelt } =
    useRegistrerVirksomhet();

  const { integrasjonsPartner, integrasjonsPartnerResolved } =
    useIntegrasjonsPartner();

  const submitKontaktinfo = (manuellKontaktInfo: ManuellKontaktinfo) => {
    setKontaktInfo(manuellKontaktInfo);
    nextStep();
  };

  const registrer = useCallback(() => {
    if (kontaktInfo) {
      analyticsAction(
        "bruker manuell registrering",
        integrasjonsPartner
          ? AnalyticsCategory.PARTNERONBOARDING
          : AnalyticsCategory.REGISTRERING,
      );
      registrerVirksomhetManuelt(kontaktInfo, nextStep);
    } else {
      setCustomError(
        new Error("Kontaktinfo mangler ved registrering av virksomhet"),
      );
    }
  }, [
    kontaktInfo,
    integrasjonsPartner,
    registrerVirksomhetManuelt,
    nextStep,
    setCustomError,
  ]);

  if (!integrasjonsPartnerResolved || isRegistering) {
    return <LoadingPage />;
  }

  return (
    <main className={styles.registreringContent}>
      <Outlet
        context={
          {
            kontaktInfo,
            submitKontaktinfo,
            integrasjonsPartner,
            registrer,
            previousStep,
            nextStep,
            reset,
          } satisfies ManuellRegistreringContextType
        }
      />
    </main>
  );
};

export default ManuellRegistreringsflyt;
